import React, { forwardRef, useCallback, useEffect, useImperativeHandle, useRef, useState } from 'react';
import { loadTurnstileScript } from '../utils/turnstileScript';

const TOKEN_WAIT_TIMEOUT_MS = 15000;

function settleWaiters(waitersRef, value) {
  const waiters = waitersRef.current;
  waitersRef.current = [];
  waiters.forEach((resolve) => resolve(value));
}

/**
 * Thin wrapper around Cloudflare Turnstile (explicit render).
 *
 * Imperative handle:
 *   getToken(): Promise<string|null> — resolves with the current token, waits
 *     briefly for a solve in progress, and resolves null when the widget is
 *     disabled (no REACT_APP_TURNSTILE_SITE_KEY), has terminally failed, or
 *     times out. A failed load/error state is retried on the next getToken()
 *     call so a transient network failure does not dead-end the user.
 *   reset(): clears the token and re-renders the challenge.
 *
 * Tokens are single-use and expire after 300s; call reset() after a failed
 * verification and re-submit.
 */
const TurnstileWidget = forwardRef(function TurnstileWidget({ theme = 'auto', className }, ref) {
  const siteKey = process.env.REACT_APP_TURNSTILE_SITE_KEY;
  const containerRef = useRef(null);
  const widgetIdRef = useRef(null);
  const tokenRef = useRef(null);
  const waitersRef = useRef([]);
  const cancelledRef = useRef(false);
  const statusRef = useRef(siteKey ? 'loading' : 'disabled');
  const [, setStatus] = useState(siteKey ? 'loading' : 'disabled');

  const mountWidget = useCallback(() => {
    if (!siteKey || widgetIdRef.current !== null) return;
    loadTurnstileScript()
      .then((turnstile) => {
        if (cancelledRef.current || !containerRef.current || widgetIdRef.current !== null) return;
        statusRef.current = 'loading';
        setStatus('loading');
        widgetIdRef.current = turnstile.render(containerRef.current, {
          sitekey: siteKey,
          theme,
          callback: (token) => {
            tokenRef.current = token;
            settleWaiters(waitersRef, token);
            statusRef.current = 'ready';
            setStatus('ready');
          },
          'expired-callback': () => {
            tokenRef.current = null;
            statusRef.current = 'loading';
            setStatus('loading');
          },
          'error-callback': () => {
            tokenRef.current = null;
            settleWaiters(waitersRef, null);
            statusRef.current = 'error';
            setStatus('error');
          },
        });
      })
      .catch(() => {
        if (cancelledRef.current) return;
        settleWaiters(waitersRef, null);
        statusRef.current = 'error';
        setStatus('error');
      });
  }, [siteKey, theme]);

  useEffect(() => {
    cancelledRef.current = false;
    mountWidget();
    return () => {
      cancelledRef.current = true;
      settleWaiters(waitersRef, null);
      const turnstile = window.turnstile;
      if (turnstile && widgetIdRef.current !== null) {
        try { turnstile.remove(widgetIdRef.current); } catch (_) { /* widget already gone */ }
      }
      widgetIdRef.current = null;
    };
  }, [mountWidget]);

  useImperativeHandle(ref, () => ({
    getToken() {
      if (!siteKey) return Promise.resolve(null);
      if (tokenRef.current) return Promise.resolve(tokenRef.current);
      if (statusRef.current === 'error') {
        const turnstile = window.turnstile;
        if (turnstile && widgetIdRef.current !== null) {
          try { turnstile.remove(widgetIdRef.current); } catch (_) { /* widget already gone */ }
          widgetIdRef.current = null;
        }
        mountWidget();
      }
      return new Promise((resolve) => {
        const timer = setTimeout(() => {
          waitersRef.current = waitersRef.current.filter((w) => w !== settle);
          resolve(null);
        }, TOKEN_WAIT_TIMEOUT_MS);
        const settle = (token) => { clearTimeout(timer); resolve(token); };
        waitersRef.current.push(settle);
      });
    },
    reset() {
      tokenRef.current = null;
      const turnstile = window.turnstile;
      if (turnstile && widgetIdRef.current !== null) {
        try { turnstile.reset(widgetIdRef.current); } catch (_) { /* widget already gone */ }
      }
    },
  }), [siteKey, mountWidget]);

  if (!siteKey) return null;
  return <div ref={containerRef} className={className} data-testid="turnstile-widget" />;
});

export default TurnstileWidget;
